import React, { useMemo, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { ThemeProvider as StyledProvider } from 'styled-components';
import { ThemeProvider as MuiProvider } from '@material-ui/core/styles';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getStyledTheme } from 'src/theme/builders/styled';
import { getMuiTheme } from 'src/theme/builders/mui';
import { getColors, isColorDark, shadows, fonts as baseFonts, buildScale } from 'src/theme';
import { ProductCardButtonOption, RadiusOption, TextTransformOption } from 'src/gql/graphql';
import { ColorsProvider } from 'contexts/colors';
import useUI from 'hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import PublicEnv from 'shared/utils/public-env';
import themes from 'theme/constants/demo';

import { CuraleafFonts } from 'shared/utils/customize';
import { defaultTagColors } from 'shared/constants/menu-colors';
import { mergeWithoutNull } from 'src/utils/misc-utils';

import { RedesignVariables } from 'src/home/redesign/redesign-variables';

import { GlobalFontStyles } from '../constants/global-styles';
import { CoreMenuVariables } from '../core-menu-variables';
import { CommerceComponentsContext } from './commerce-components/context';
import { useRemoteCommerceComponents } from './commerce-components/remote-theme';

import {
  getEncodedFonts,
  getFullFontFamily,
  getRadiusStyle,
  getTextTransformStyle,
  getWebCustomizationSettings,
} from '../theme.utils';

export function ThemeProvider(props) {
  const { children, deviceType = 'desktop' } = props;
  const cart = useCart();
  const useDispensaryHook = useDispensary();
  const UI = useUI();

  const flags = useFlags();
  const isCuraleafFontsEnabled = flags['growth.ecomm.curaleaf-fonts.entitlement'] ?? false;

  let {
    query: { cName },
  } = useRouter();

  const router = useRouter();
  const isEmbedded = useObserver(() => UI.isEmbedded);
  const isDutchieMain = useObserver(() => UI.isDutchieMain);
  const isInstoreEnrollmentRoute = router?.pathname.includes('/instore-enrollment');
  const isEnterpriseEnrollmentRoute = router?.pathname.includes('/enterprise/');
  const isKiosk = useObserver(() => UI.isKiosk);
  const isMobileEcommApp = useObserver(() => UI.isMobileEcommApp);
  const dispensaryFromCart = useObserver(() => cart.dispensary);
  const dispensaryFromHook = useObserver(() => useDispensaryHook.dispensary);
  const dispensaryFromEnterprise = useObserver(() => UI.enterpriseDispensary);
  const dispensary = dispensaryFromCart || dispensaryFromHook || dispensaryFromEnterprise;

  const encodedFontsRef = useRef();

  const { getCommerceComponents, remoteThemeModule } = useRemoteCommerceComponents(dispensary);

  if (!cName && dispensary) {
    cName = dispensary.cName;
  }

  const shouldUseDemoSkins = PublicEnv.appEnv === 'demo' || PublicEnv.appEnv === 'development';

  // memoize the themes so they don't get regenerated
  const stores = useMemo(() => {
    // when we're outside of embedded or don't know what dispensary we're in
    // set theme to default and load from there...
    if (!shouldUseDemoSkins && !dispensary && isEmbedded) {
      // isEmbedded should always have a dispensary, so lets wait for one...
      return null;
    }

    if (!dispensary && isKiosk) {
      // wait for dispensary in kiosk mode too, we need this for high performance mode to *always* work
      return null;
    }

    let themecName = cName;

    if (!shouldUseDemoSkins || !isEmbedded || !isMobileEcommApp || !themecName) {
      themecName = `default`;
    }

    const theme = themes[themecName] || themes.default;
    const colors = { ...getColors(), ...theme.colors };

    let customized = {
      colors: {
        buttonsLinks: isDutchieMain ? `#4d2a3d` : colors.primaryBlue,
        navBar: colors.primaryGrey,
        staffPickTag: defaultTagColors.staffPick,
        discountTag: defaultTagColors.discount,
        productCardButton: ProductCardButtonOption.tinted,
      },
      fonts: {
        primary: baseFonts.join(', '),
        secondary: baseFonts.join(', '),
      },
      textTransforms: {
        buttons: getTextTransformStyle(TextTransformOption.upper),
        links: getTextTransformStyle(TextTransformOption.title),
        tags: getTextTransformStyle(TextTransformOption.title),
      },
      radius: getRadiusStyle(RadiusOption.rounded),
      utils: {
        isNavBarDark: false,
      },
      components: {
        storeFront: {
          header: null,
          footer: null,
        },
      },
      maxSiteWidth: 1200,
    };

    if (!isDutchieMain || isInstoreEnrollmentRoute || isEnterpriseEnrollmentRoute) {
      const { colors: customColors, fonts: customFonts, textTransforms, radius } = getWebCustomizationSettings(
        dispensary
      );

      const fullFamilies = {
        primary: getFullFontFamily(customFonts.primary),
        secondary: getFullFontFamily(customFonts.secondary),
      };

      customized = mergeWithoutNull(customized, {
        colors: customColors,
        fonts: fullFamilies,
        textTransforms,
        radius,
        utils: {
          isNavBarDark: isColorDark(customColors.navBar),
        },
      });

      encodedFontsRef.current = getEncodedFonts(customFonts);
    }

    const embeddedMenu = { height: theme.banner?.height || 0 };
    const muiTheme = getMuiTheme({ colors, customized, deviceType });
    const styledTheme = getStyledTheme({
      colors,
      embeddedMenu,
      shadows,
      breakpoints: muiTheme.breakpoints,
      spaces: buildScale(4, 20),
      customized,
    });

    return {
      theme,
      colors,
      styledTheme,
      muiTheme,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cName, isEmbedded, dispensary, remoteThemeModule, isEnterpriseEnrollmentRoute, deviceType]);

  const commerceComponentsTheme = useMemo(() => getCommerceComponents(stores?.styledTheme), [
    stores?.styledTheme,
    getCommerceComponents,
  ]);

  const RemoteGlobalStyles = useMemo(() => commerceComponentsTheme.components.GlobalStyles({}), [
    commerceComponentsTheme,
  ]);

  if (!stores) {
    return null;
  }

  return (
    <>
      <ColorsProvider value={stores.colors}>
        <StyledProvider theme={stores.styledTheme}>
          <GlobalFontStyles />
          <CoreMenuVariables $isPlum={isDutchieMain} />
          {isDutchieMain && <RedesignVariables />}
          <RemoteGlobalStyles />
          {!isDutchieMain && isCuraleafFontsEnabled && <CuraleafFonts />}

          {!isDutchieMain && encodedFontsRef.current && (
            <>
              <link rel='preconnect' href='https://fonts.googleapis.com' />
              <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
              <link
                id='customFont'
                rel='stylesheet'
                href={`https://fonts.googleapis.com/css2?${encodedFontsRef.current}&display=swap`}
              />
            </>
          )}
          <MuiProvider theme={stores.muiTheme}>
            <CommerceComponentsContext.Provider value={commerceComponentsTheme}>
              {children}
            </CommerceComponentsContext.Provider>
          </MuiProvider>
        </StyledProvider>
      </ColorsProvider>
    </>
  );
}
